import { useMemo } from 'react';
import { graphql } from 'react-relay';
import { useFetchQuery } from '@atlassian/jira-relay-utils';
import type {
	getEpicsRelayQuery$variables,
	getEpicsRelayQuery$data,
} from '@atlassian/jira-relay/src/__generated__/getEpicsRelayQuery.graphql';
import { toIssueId } from '@atlassian/jira-shared-types';
import type { ServiceQueryResult } from '@atlassian/jira-software-deployments-service-query-types';
import type { Epic } from '../../common/types';
import { getResourceId } from '../../common/utils/ari';
import { useProjectAri } from './utils';

const transformEpic = (
	epic:
		| {
				readonly id: string;
				readonly summary: string | null | undefined;
		  }
		| null
		| undefined,
) => {
	if (epic == null) {
		throw Error('epic cannot be null');
	}
	return {
		id: toIssueId(getResourceId(epic.id)),
		summary: epic.summary ?? '',
	};
};

// @ExportForTesting
export const transformQueryResult = ({
	loading,
	error,
	data,
}: {
	loading: boolean;
	error: Error | null;
	data: getEpicsRelayQuery$data | null;
}): ServiceQueryResult<Epic[]> => {
	if (loading) {
		return {
			loading: true,
		};
	}
	if (error) {
		return {
			error,
			loading: false,
		};
	}
	// This is possible due to the `skip` condition on the query
	if (data == null) {
		return {
			loading: false,
		};
	}
	if (data.jiraReleases?.epics?.nodes == null) {
		return {
			loading: false,
			error: Error('Epics not found'),
		};
	}

	const transformationResult = data.jiraReleases.epics.nodes.map(transformEpic);

	return transformationResult instanceof Error
		? {
				error: transformationResult,
				loading: false,
			}
		: {
				data: transformationResult,
				loading: false,
			};
};

export const useEpics = (text: string): ServiceQueryResult<Epic[]> => {
	const projectAri = useProjectAri();
	const queryResult = useFetchQuery<
		getEpicsRelayQuery$variables,
		getEpicsRelayQuery$data,
		undefined
	>(
		graphql`
			query getEpicsRelayQuery($first: Int!, $filter: JiraReleasesEpicFilter!) {
				jiraReleases {
					epics(first: $first, filter: $filter) {
						nodes {
							id
							summary
						}
					}
				}
			}
		`,
		{
			first: 10,
			filter: {
				projectId: projectAri ?? '',
				text,
			},
		},
		{ skip: projectAri === null, fetchPolicy: 'store-or-network' },
	);

	return useMemo(() => transformQueryResult(queryResult), [queryResult]);
};
